import { showApiError, showNetworkError } from './toast';
import { logAuthError, logAuthEvent } from './sentry';
import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios';

const BASE_URL = import.meta.env.VITE_BACKEND_URL;

// Simple token storage
let accessToken: string | null = null;
let isImportingCommonApp = false;

export const setAccessToken = (token: string | null) => {
  accessToken = token;
};

export const setCommonAppImportFlag = (importing: boolean) => {
  isImportingCommonApp = importing;
  console.log(`🔄 Common App import flag set to: ${importing}`);
};

// Create Axios instance
const api: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  withCredentials: true, // Send cookies (refresh token)
  timeout: 300000, // Increased to 30 seconds
});

// Request interceptor - Add access token to requests
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (accessToken && config.headers) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle token refresh on 401
let isRefreshing = false;
let queue: ((token: string) => void)[] = [];

api.interceptors.response.use(
  (response: AxiosResponse) => {
    try {
      // Add logging for successful responses
      console.log(`✅ API Success: ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`);
      console.log(`✅ Response data type: ${typeof response.data}, length: ${JSON.stringify(response.data).length}`);
      return response;
    } catch (interceptorError) {
      console.error('❌ Response interceptor error:', interceptorError);
      // If there's an error in the interceptor, still return the response
      return response;
    }
  },
  async (error) => {
    // Add comprehensive error logging
    console.log(`❌ API Error: ${error.config?.method?.toUpperCase()} ${error.config?.url}`, {
      status: error.response?.status,
      statusText: error.response?.statusText,
      message: error.message,
      code: error.code,
      responseData: error.response?.data
    });
    const errorContext = {
      status: error.response?.status,
      message: error.response?.data?.message,
      url: error.config?.url,
      method: error.config?.method,
      hasAccessToken: !!accessToken,
      timestamp: new Date().toISOString()
    };
    
    console.log("API Error:", error.response?.status, error.response?.data?.message);
    logAuthError(error, errorContext);
    
    // CENTRALIZED ERROR HANDLING - All 401s go here first
    
    // Check for specific "No refresh token found" error - ABORT EVERYTHING
    if (error.response?.status === 401 && error.response?.data?.message === "No refresh token found") {
      console.log("No refresh token found - redirecting to login immediately");
      logAuthEvent('no_refresh_token_logout', { 
        reason: 'No refresh token found',
        userAgent: navigator.userAgent,
        cookies: document.cookie
      });
      
      setAccessToken(null);
      localStorage.removeItem('cached_user');
      
      // Clear any pending requests
      queue.forEach((cb) => cb(''));
      queue = [];
      
      // Only redirect if not already on login page and not during Common App import
      if (typeof window !== 'undefined' && 
          !window.location.pathname.includes('/login') && 
          !window.location.pathname.includes('/settings') &&
          !isImportingCommonApp) {
        window.location.replace('/login');
      }
      
      return Promise.reject(error);
    }

    const originalRequest = error.config;

    // Handle other 401s with refresh logic (only if not already retried)
    if (error.response?.status === 401 && !originalRequest._retry) {
      console.log("401 error - attempting token refresh");
      logAuthEvent('token_refresh_attempt', { 
        originalUrl: originalRequest.url,
        isRefreshing,
        queueLength: queue.length 
      });
      
      if (isRefreshing) {
        // Queue pending requests until refresh completes
        return new Promise((resolve) => {
          queue.push((token: string) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            resolve(api(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Call refresh endpoint (cookie sent automatically)
        const res = await api.post('/api/v1/auth/refresh');
        const newToken = res.data.access_token;
        setAccessToken(newToken);

        logAuthEvent('token_refresh_success', { 
          queueLength: queue.length,
          hasNewToken: !!newToken 
        });

        // Replay queued requests
        queue.forEach((cb) => cb(newToken));
        queue = [];
        
        // Retry original request
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }
        return api(originalRequest);
      } catch (err: any) {
        console.log("Token refresh failed:", err.response?.data?.message);
        logAuthEvent('token_refresh_failed', { 
          error: err.response?.data?.message,
          status: err.response?.status,
          userAgent: navigator.userAgent
        });
        
        // Refresh failed, clear token and redirect to login
        setAccessToken(null);
        localStorage.removeItem('cached_user');
        
        // Clear any pending requests
        queue.forEach((cb) => cb(''));
        queue = [];
        
        // Only redirect if not already on login page and not during Common App import
        if (typeof window !== 'undefined' && 
            !window.location.pathname.includes('/login') && 
            !window.location.pathname.includes('/settings') &&
            !isImportingCommonApp) {
          window.location.replace('/login');
        }
        
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    // For all other errors, just reject
    return Promise.reject(error);
  }
);

// API Response types
export interface ApiResponse<T = any> {
  data: T;
  message?: string;
  success: boolean;
}

export interface TokenResponse {
  token_count: number;
  payment_details: {
    is_subscribed: boolean;
    token_count: number;
  };
}

export interface DraftResponse {
  id: string;
  content: string;
  university: string;
  timestamp: Date;
}

export interface CollegeResponse {
  id: string;
  name: string;
  location: string;
  acceptance_rate: number;
  tuition: number;
  ranking: number;
}

// All API endpoints
export const API = {
  BASE_URL,
  GET_ALL_DRAFTS: `${BASE_URL}/api/v1/get-all-drafts`,
  GET_TOKENS: `${BASE_URL}/get-tokens`,
  VALIDATE_SUBSCRIPTION: (userId: string) => `${BASE_URL}/validate-subscription/${userId}`,
  ANALYZE: `${BASE_URL}/api/v1/analysis/`,
  UPDATE_TOKENS: `${BASE_URL}/update-tokens`,
  SAVE_DRAFT: `${BASE_URL}/api/v1/save-draft`,
  GET_ALL_COLLEGES: `${BASE_URL}/api/v1/get-all-colleges`,
  USER_UNIVERSITIES: `${BASE_URL}/api/v1/user-universities/my-universities`,
  FIREBASE_AUTH: `${BASE_URL}/api/v1/auth/firebase`,
  REFRESH_TOKEN: `${BASE_URL}/api/v1/auth/refresh`,
  LOGOUT: `${BASE_URL}/api/v1/auth/logout`,
  UNIVERSITIES: `${BASE_URL}/api/v1/universities`,
  UNIVERSITIES_ALL: `${BASE_URL}/api/v1/universities/all`,
  USER_UNIVERSITIES_CRUD: `${BASE_URL}/api/v1/user-universities`,
  ME: `${BASE_URL}/api/v1/auth/me`
} as const;

// Export the Axios instance for direct use
export default api; 
